import { ReactNode } from 'react'
import { FormGroup, Modal } from 'react-bootstrap'
import FormHandler from '../../utils/Form/FormHandler'
import Field from '../../utils/Form/Widgets/Field'
import Select from '../../utils/Form/Widgets/Select'
import FileInput from '../../utils/Form/Widgets/FileInput'
import Check from '../../utils/Form/Widgets/Check'
import AutocompleteSelect from '../../utils/Form/Widgets/AutocompleteSelect'

type Props = {
  onHide: () => void
  show: boolean
  itemIdForUpdate?: string
  refreshFunction?: Function
  canDelete?: boolean
}

class Company {
  name?: string
  phone?: string
  logo?: number
  sole_trader?: boolean
  fiscal_day?: number
  fiscal_month?: number
  sic_code?: string
  sic_code_status?: boolean
  number_of_empleyees?: number
  status?: string
  is_reseller?: number
  target_consumption_ratio?: number
  alerts_threshold_value?: number
  dashboard_settings?: string
  alarm_type?: string
  alarm_notifications_by_email?: boolean
}

class CompanyEditModal extends FormHandler {
  statusOptions = [
    {
      value: 'active',
      label: 'Active',
    },
    {
      value: 'passive',
      label: 'Disabled',
    },
  ]

  isResellerOptions = [
    { value: '1', label: 'Yes' },
    { value: '0', label: 'No' },
  ]

  alarmTypeOptions = [
    { value: 'hourly', label: 'Hourly' },
    { value: 'daily', label: 'Daily' },
    { value: 'both', label: 'Both' },
  ]

  constructor(props: Props) {
    super(props)
    this.state = {
      entity: new Company(),
    }
  }

  componentDidMount() {
    super.componentDidMount()
    // Parse dashboard_settings when component mounts
    this.parseDashboardSettings()
  }

  componentDidUpdate(prevProps: Props, prevState: any) {
    // Parse dashboard_settings when entity data is loaded
    if (prevState.entity !== this.state.entity && this.state.entity) {
      this.parseDashboardSettings()
    }
  }

  parseDashboardSettings() {
    const entity = this.state.entity as any
    if (entity?.dashboard_settings) {
      try {
        const dashboardSettings = JSON.parse(entity.dashboard_settings)
        this.setState({
          dashboardSettings: {
            gasCharts: dashboardSettings.gasCharts || 'enabled',
            electricCharts: dashboardSettings.electricCharts || 'enabled',
          },
        })
      } catch (error) {
        console.error('Error parsing dashboard_settings:', error)
        this.setState({
          dashboardSettings: {
            gasCharts: 'enabled',
            electricCharts: 'enabled',
          },
        })
      }
    } else {
      // Default values when no dashboard_settings exist
      this.setState({
        dashboardSettings: {
          gasCharts: 'enabled',
          electricCharts: 'enabled',
        },
      })
    }
  }

  handleDashboardSettingChange = (field: string, isChecked: boolean) => {
    const currentState = this.state as any
    const value = isChecked ? 'enabled' : 'disabled'

    const newDashboardSettings = {
      ...currentState.dashboardSettings,
      [field]: value,
    }

    // Update dashboard settings state
    this.setState({
      dashboardSettings: newDashboardSettings,
    })

    // Update entity with JSON string
    const updatedEntity = {
      ...currentState.entity,
      dashboard_settings: JSON.stringify(newDashboardSettings),
    }

    this.setState({
      entity: updatedEntity,
    })
  }

  getApiUrl(): string {
    return '/company/save/' + this.props.itemIdForUpdate
  }

  getSingleUrl(): string | null {
    if (this.props.itemIdForUpdate === 'add') {
      return null
    } else {
      return '/company/single/' + this.props.itemIdForUpdate
    }
  }

  getPostData(): object {
    return {
      companies: this.state.entity,
    }
  }

  getFiscalDayOptions() {
    return Array.from(Array(31).keys()).map((i) => ({
      value: (i + 1).toString(),
      label: (i + 1).toString(),
    }))
  }

  getFiscalMonthOptions() {
    const monthNames = [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December',
    ]
    return Array.from(Array(12).keys()).map((i) => ({
      value: (i + 1).toString(),
      label: monthNames[i],
    }))
  }

  render(): ReactNode {
    var state = this.state as any
    let title = 'Add Company'
    if (this.state.entity && this.props.itemIdForUpdate !== 'add') {
      let entity = this.state.entity as any
      title = entity.name
    }
    let defaultCountryValue = state.entity?.company_addresses?.[0]?.country || 0
    if (this.props.itemIdForUpdate === 'add' || !state.entity?.company_addresses?.[0]) {
      // Default United Kingdom
      defaultCountryValue = 231
    } else {
      defaultCountryValue = state.entity?.company_addresses?.[0]?.country
    }

    return (
      <Modal
        id='kt_modal_create_company'
        aria-hidden='true'
        dialogClassName='modal-dialog modal-dialog-centered'
        size='lg'
        show={this.props.show}
        backdrop={true}
        onHide={this.props.onHide}
      >
        <form
          onSubmit={(event) => {
            this.handleSubmit(event).then((response) => {
              if (this.props.refreshFunction) {
                this.props.refreshFunction(response.data.data.entity_id)
              }
            })
          }}
        >
          <Modal.Header closeButton>
            <Modal.Title>{title}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <FormGroup>
              <Field
                title='Name'
                className='form-control'
                type='text'
                name='name'
                onInput={(event: any) => {
                  this.handleInputChange(event)
                }}
                defaultValue={state.entity.name}
              />
            </FormGroup>
            <FormGroup>
              <FileInput
                fileUploadKeyUrl='/company/getCompanyLogoUploadKey'
                title='Company Logo'
                name='logo'
                onChange={(event) => {
                  this.handleInputChange(event)
                }}
                defaultValue={state.entity.logo}
                accept='image/*'
              />
            </FormGroup>
            <FormGroup>
              <Select
                title='Fiscal Day'
                name='fiscal_day'
                onChange={(event: any) => {
                  this.handleInputChange(event)
                }}
                options={this.getFiscalDayOptions()}
                defaultValue={state.entity.fiscal_day}
              />
            </FormGroup>
            <FormGroup>
              <Select
                title='Fiscal Month'
                name='fiscal_month'
                onChange={(event: any) => {
                  this.handleInputChange(event)
                }}
                options={this.getFiscalMonthOptions()}
                defaultValue={state.entity.fiscal_month}
              />
            </FormGroup>
            <FormGroup>
              <Field
                title='Number Of Employees'
                className='form-control'
                min={0}
                name='number_of_empleyees'
                pattern='^\d+$'
                onInput={(event: any) => {
                  this.handleInputChange(event)
                }}
                defaultValue={state.entity.number_of_empleyees}
              />
            </FormGroup>
            <FormGroup>
              <Check
                title='Sole Trader'
                name='sole_trader'
                type='switch'
                checked={state.entity.sole_trader ? true : false}
                options={['']}
                onChange={(event: any) => {
                  this.handleInputChange(event)
                }}
              />
            </FormGroup>
            <FormGroup>
              <Check
                title='Is Reseller'
                name='is_reseller'
                type='switch'
                checked={state.entity.is_reseller ? true : false}
                options={['']}
                onChange={(event: any) => {
                  this.handleInputChange(event)
                }}
              />
            </FormGroup>
            <FormGroup>
              <Field
                title='Address'
                className='form-control'
                type='string'
                name='company_addresses.0.address'
                onInput={(event: any) => {
                  this.handleInputChange(event)
                }}
                defaultValue={state.entity?.company_addresses?.[0]?.address}
              />
            </FormGroup>
            <FormGroup>
              <Field
                title='City'
                className='form-control'
                type='string'
                pattern='^([A-Za-z \g])+$'
                name='company_addresses.0.city'
                onInput={(event: any) => {
                  this.handleInputChange(event)
                }}
                defaultValue={state.entity?.company_addresses?.[0]?.city}
              />
            </FormGroup>
            <FormGroup>
              <Field
                title='County'
                className='form-control'
                type='string'
                name='company_addresses.0.county'
                pattern='^([A-Za-z \g])+$'
                onInput={(event: any) => {
                  this.handleInputChange(event)
                }}
                defaultValue={state.entity?.company_addresses?.[0]?.county}
              />
            </FormGroup>
            <FormGroup>
              <AutocompleteSelect
                title='Country'
                name='company_addresses.0.country'
                onChange={(event: any) => {
                  this.handleInputChange(event)
                }}
                defaultValue={defaultCountryValue}
                autoCompleteTokenUrl='/company/countriesautocomplete'
              />
            </FormGroup>
            <FormGroup>
              <Field
                title='Postcode'
                className='form-control'
                type='string'
                name='company_addresses.0.postcode'
                onInput={(event: any) => {
                  this.handleInputChange(event)
                }}
                defaultValue={state.entity?.company_addresses?.[0]?.postcode}
              />
            </FormGroup>
            <FormGroup>
              <Field
                title='Address Contact Phone'
                className='form-control'
                name='company_addresses.0.phone'
                pattern='^\d+$'
                onInput={(event: any) => {
                  this.handleInputChange(event)
                }}
                defaultValue={state.entity?.company_addresses?.[0]?.phone}
              />
            </FormGroup>
            <FormGroup>
              <Field
                title='Email'
                className='form-control'
                type='string'
                name='company_addresses.0.email'
                onInput={(event: any) => {
                  this.handleInputChange(event)
                }}
                defaultValue={state.entity?.company_addresses?.[0]?.email}
              />
            </FormGroup>
            <FormGroup>
              <Field
                title='Target Consumption Ratio (%)'
                className='form-control'
                type='string'
                name='target_consumption_ratio'
                onInput={(event: any) => {
                  this.handleInputChange(event)
                }}
                defaultValue={state.entity.target_consumption_ratio}
              />
            </FormGroup>
            <hr />
            <h5>Alerts Settings</h5>
            <FormGroup>
              <Field
                title='Alerts Threshold Value (%)'
                className='form-control'
                type='string'
                name='alerts_threshold_value'
                onInput={(event: any) => {
                  this.handleInputChange(event)
                }}
                defaultValue={
                  state.entity.alerts_threshold_value !== 0
                    ? state.entity.alerts_threshold_value
                    : 15
                }
              />
            </FormGroup>
            <FormGroup>
              <Select
                title='Alarm Type'
                name='alarm_type'
                options={this.alarmTypeOptions}
                defaultValue={state.entity.alarm_type}
                onChange={(event: any) => {
                  this.handleInputChange(event)
                }}
              />
            </FormGroup>
            <FormGroup>
              <Check
                title='Alerts Notifications By Email'
                name='alarm_notifications_by_email'
                type='switch'
                checked={state.entity.alarm_notifications_by_email ? true : false}
                options={['']}
                onChange={(event: any) => {
                  this.handleInputChange(event)
                }}
              />
            </FormGroup>

            {/* Dashboard Settings Section */}
            <hr />
            <h5>Dashboard Settings</h5>
            <FormGroup>
              <Check
                title='Gas Charts'
                name='gasCharts'
                type='switch'
                checked={state.dashboardSettings?.gasCharts === 'enabled'}
                options={['']}
                onChange={(event: any) => {
                  this.handleDashboardSettingChange('gasCharts', event.target.checked)
                }}
              />
            </FormGroup>
            <FormGroup>
              <Check
                title='Electric Charts'
                name='electricCharts'
                type='switch'
                checked={state.dashboardSettings?.electricCharts === 'enabled'}
                options={['']}
                onChange={(event: any) => {
                  this.handleDashboardSettingChange('electricCharts', event.target.checked)
                }}
              />
            </FormGroup>

            <FormGroup>
              <Select
                title='Status'
                name='status'
                options={this.statusOptions}
                defaultValue={state.entity.status}
                onChange={(event: any) => {
                  this.handleInputChange(event)
                }}
              />
            </FormGroup>
          </Modal.Body>
          <Modal.Footer className='justify-content-end'>
            {this.props.itemIdForUpdate !== 'add' && this.props.canDelete ? (
              <button
                type='button'
                onClick={(event) => {
                  this.handleDelete(event).then((response) => {
                    if (this.props.refreshFunction) {
                      this.props.refreshFunction('')
                    }
                  })
                }}
                className='btn btn-danger btn-sm'
              >
                <span className='fa fa-trash'></span>
                Delete
              </button>
            ) : (
              <></>
            )}
            <button type='submit' className='btn btn-success btn-sm'>
              Save
            </button>
          </Modal.Footer>
        </form>
      </Modal>
    )
  }
}

export { CompanyEditModal }
